import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useToast } from '../../ui/use-toast';
import {
  BarChart3,
  PieChart as PieChartIcon,
  TrendingUp,
  TrendingDown,
  Users,
  Building,
  Award,
  Download,
  Filter,
  RefreshCw,
  Target,
  Zap,
  Brain
} from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const DashboardAnalytics = () => {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroArea, setFiltroArea] = useState('todas');
  const [filtroCargo, setFiltroCargo] = useState('todos');
  const [filtroTempo, setFiltroTempo] = useState('todos');
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadDashboardData();
  }, [filtroArea, filtroCargo, filtroTempo]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Carregar dados dos resultados
      const resultadosResponse = await axios.get(`${backendUrl}/api/fit-cultural/questionario/resultados`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Carregar analytics por área
      let analyticsResponse;
      if (filtroArea !== 'todas') {
        // Buscar área específica
        analyticsResponse = await axios.get(
          `${backendUrl}/api/fit-cultural/analytics/area/${filtroArea}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
      } else {
        // Buscar comparativo geral
        analyticsResponse = await axios.get(
          `${backendUrl}/api/fit-cultural/analytics/comparativo`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
      }

      // Processar dados para gráficos
      const processedData = processarDadosParaGraficos(resultadosResponse.data, analyticsResponse.data);
      setDados(processedData);

    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do dashboard",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const processarDadosParaGraficos = (resultados, analytics) => {
    // Classificar resultados por faixa
    const distribuicaoFaixas = { Baixo: 0, Médio: 0, Alto: 0 };
    const scoresPorMes = {};
    const distribuicaoAreas = {};
    const tendenciaTemporal = [];
    const funcionariosPorScore = {};

    resultados.forEach(resultado => {
      const score = resultado.score_final || 0;
      let faixa = 'Médio';
      if (score <= 30) faixa = 'Baixo';
      else if (score > 60) faixa = 'Alto';
      
      distribuicaoFaixas[faixa]++;

      // Scores por mês
      if (resultado.finalizada_em) {
        const mes = new Date(resultado.finalizada_em).toISOString().slice(0, 7); // YYYY-MM
        if (!scoresPorMes[mes]) scoresPorMes[mes] = [];
        scoresPorMes[mes].push(score);
      }

      // Agrupar por funcionário para simular áreas
      const funcionario = resultado.funcionario_nome || 'Funcionário';
      if (!funcionariosPorScore[funcionario]) {
        funcionariosPorScore[funcionario] = {
          scores: [],
          total: 0
        };
      }
      funcionariosPorScore[funcionario].scores.push(score);
      funcionariosPorScore[funcionario].total++;
    });

    // Preparar dados das áreas
    if (analytics.areas && analytics.areas.length > 0) {
      // Resposta do endpoint comparativo
      analytics.areas.forEach(areaData => {
        distribuicaoAreas[areaData.area] = {
          area: areaData.area,
          total: areaData.total_avaliacoes,
          score_medio: Math.round(areaData.ifc_medio),
          taxa_aprovacao: Math.round(areaData.taxa_aprovacao),
          classificacoes: {
            Alto: Math.round((areaData.fit_alto / areaData.total_avaliacoes) * 100),
            Médio: Math.round((areaData.fit_medio / areaData.total_avaliacoes) * 100),
            Baixo: Math.round((areaData.fit_baixo / areaData.total_avaliacoes) * 100)
          }
        };
      });
    } else if (analytics.area) {
      // Resposta de área específica
      distribuicaoAreas[analytics.area] = {
        area: analytics.area,
        total: analytics.total_avaliacoes,
        score_medio: Math.round(analytics.ifc_medio),
        taxa_aprovacao: Math.round(analytics.taxa_aprovacao),
        classificacoes: {
          Alto: Math.round((analytics.fit_alto / analytics.total_avaliacoes) * 100),
          Médio: Math.round((analytics.fit_medio / analytics.total_avaliacoes) * 100),
          Baixo: Math.round((analytics.fit_baixo / analytics.total_avaliacoes) * 100)
        }
      };
    } else {
      // Fallback: usar dados por funcionário como "áreas"
      Object.entries(funcionariosPorScore).forEach(([funcionario, data]) => {
        const scoreMediao = data.scores.reduce((sum, score) => sum + score, 0) / data.scores.length;
        const aprovados = data.scores.filter(score => score > 60).length;
        
        distribuicaoAreas[funcionario] = {
          area: funcionario,
          total: data.total,
          score_medio: Math.round(scoreMediao),
          taxa_aprovacao: Math.round((aprovados / data.total) * 100),
          classificacoes: {
            Alto: data.scores.filter(s => s > 60).length,
            Médio: data.scores.filter(s => s > 30 && s <= 60).length,
            Baixo: data.scores.filter(s => s <= 30).length
          }
        };
      });
    }

    // Tendência temporal
    Object.entries(scoresPorMes).forEach(([mes, scores]) => {
      const media = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      tendenciaTemporal.push({
        mes,
        score_medio: Math.round(media),
        total_avaliacoes: scores.length
      });
    });

    // Dados para gráfico de pizza
    const dadosPizza = [
      { name: 'Fit Alto', value: distribuicaoFaixas.Alto, color: '#22c55e', percentage: 0 },
      { name: 'Fit Médio', value: distribuicaoFaixas.Médio, color: '#eab308', percentage: 0 },
      { name: 'Fit Baixo', value: distribuicaoFaixas.Baixo, color: '#ef4444', percentage: 0 }
    ];

    const total = dadosPizza.reduce((sum, item) => sum + item.value, 0);
    dadosPizza.forEach(item => {
      item.percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
    });

    // Dados para gráfico de barras (áreas)
    const dadosBarras = Object.values(distribuicaoAreas).map(area => ({
      ...area,
      nome: area.area.length > 15 ? area.area.substring(0, 15) + '...' : area.area
    }));

    // Estatísticas gerais
    const estatisticas = {
      total_avaliacoes: resultados.length,
      score_medio_geral: total > 0 ? Math.round(resultados.reduce((sum, r) => sum + (r.score_final || 0), 0) / resultados.length) : 0,
      taxa_aprovacao_geral: total > 0 ? Math.round((distribuicaoFaixas.Alto / total) * 100) : 0,
      areas_ativas: Object.keys(distribuicaoAreas).length,
      melhor_area: dadosBarras.length > 0 ? dadosBarras.reduce((best, area) => area.score_medio > best.score_medio ? area : best) : null,
      tendencia: tendenciaTemporal.length > 1 ? 
        (tendenciaTemporal[tendenciaTemporal.length - 1].score_medio > tendenciaTemporal[tendenciaTemporal.length - 2].score_medio ? 'up' : 'down') : 'stable'
    };

    return {
      distribuicaoPizza: dadosPizza,
      distribuicaoBarras: dadosBarras,
      tendenciaTemporal: tendenciaTemporal.sort((a, b) => a.mes.localeCompare(b.mes)),
      estatisticas,
      dadosRaw: resultados,
      analyticsRaw: analytics
    };
  };

  const exportarPDF = async () => {
    try {
      setExporting(true);
      const element = document.getElementById('dashboard-analytics');
      const canvas = await html2canvas(element, { 
        scale: 2,
        logging: false,
        useCORS: true 
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      
      const imgWidth = 280;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save(`dashboard-fit-cultural-${new Date().toISOString().slice(0, 10)}.pdf`);
      
      toast({
        title: "Sucesso",
        description: "Relatório PDF exportado com sucesso!"
      });
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast({
        title: "Erro",
        description: "Erro ao exportar PDF",
        variant: "destructive"
      });
    } finally {
      setExporting(false);
    }
  };

  const exportarExcel = () => {
    try {
      setExporting(true);
      
      // Preparar dados para Excel
      const dadosExcel = dados.dadosRaw.map(resultado => ({
        'Funcionário': resultado.funcionario_nome,
        'Avaliação': resultado.avaliacao_nome,
        'Score Final (%)': resultado.score_final,
        'Classificação': resultado.score_final <= 30 ? 'Baixo' : resultado.score_final <= 60 ? 'Médio' : 'Alto',
        'Data Finalização': resultado.finalizada_em ? new Date(resultado.finalizada_em).toLocaleDateString('pt-BR') : 'N/A',
        'Total Perguntas': resultado.resultado?.total_perguntas || 0
      }));

      const worksheet = XLSX.utils.json_to_sheet(dadosExcel);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Resultados Fit Cultural");
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      saveAs(data, `resultados-fit-cultural-${new Date().toISOString().slice(0, 10)}.xlsx`);
      
      toast({
        title: "Sucesso", 
        description: "Relatório Excel exportado com sucesso!"
      });
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast({
        title: "Erro",
        description: "Erro ao exportar Excel",
        variant: "destructive"
      });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-lg">Carregando analytics...</span>
      </div>
    );
  }

  if (!dados) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <BarChart3 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">Nenhum dado encontrado</h3>
          <p className="text-gray-500">Aplique algumas avaliações para ver os analytics</p>
        </CardContent>
      </Card>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium">{`${label}: ${payload[0].value}%`}</p>
          {payload[0].payload.percentage && (
            <p className="text-sm text-gray-600">{`${payload[0].payload.percentage}% do total`}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div id="dashboard-analytics" className="space-y-8">
      {/* Header com filtros e exports */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Analytics</h1>
          <p className="text-gray-600">Análise completa dos resultados do Fit Cultural</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Filtros */}
          <div className="flex gap-2">
            <Select value={filtroArea} onValueChange={setFiltroArea}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Área" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as Áreas</SelectItem>
                {dados?.analyticsRaw?.areas ? 
                  dados.analyticsRaw.areas.map(areaData => (
                    <SelectItem key={areaData.area} value={areaData.area}>{areaData.area}</SelectItem>
                  )) : 
                  dados?.analyticsRaw?.area ? (
                    <SelectItem key={dados.analyticsRaw.area} value={dados.analyticsRaw.area}>{dados.analyticsRaw.area}</SelectItem>
                  ) : null
                }
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm" onClick={loadDashboardData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>

          {/* Exports */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportarPDF} disabled={exporting}>
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <Button variant="outline" size="sm" onClick={exportarExcel} disabled={exporting}>
              <Download className="w-4 h-4 mr-2" />
              Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Users className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{dados.estatisticas.total_avaliacoes}</p>
                <p className="text-sm text-gray-600">Total Avaliações</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Target className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{dados.estatisticas.score_medio_geral}%</p>
                <p className="text-sm text-gray-600">Score Médio Geral</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Award className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold">{dados.estatisticas.taxa_aprovacao_geral}%</p>
                <p className="text-sm text-gray-600">Taxa Fit Alto</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Building className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">{dados.estatisticas.areas_ativas}</p>
                <p className="text-sm text-gray-600">Áreas Ativas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos Principais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Gráfico de Pizza - Distribuição por Faixa */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <PieChartIcon className="w-5 h-5" />
              <span>Distribuição por Faixa de Fit</span>
            </CardTitle>
            <CardDescription>
              Classificação dos funcionários por nível de fit cultural
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dados.distribuicaoPizza}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name} (${percentage}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {dados.distribuicaoPizza.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            <div className="flex justify-center space-x-6 mt-4">
              {dados.distribuicaoPizza.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div 
                    className="w-4 h-4 rounded" 
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-sm">{item.name}</span>
                  <Badge variant="secondary">{item.value}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Gráfico de Barras - Performance por Área */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5" />
              <span>Performance por Área</span>
            </CardTitle>
            <CardDescription>
              Score médio e taxa de aprovação por área
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dados.distribuicaoBarras}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nome" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="score_medio" fill="#3b82f6" name="Score Médio (%)" />
                <Bar dataKey="taxa_aprovacao" fill="#10b981" name="Taxa Aprovação (%)" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Tendência Temporal */}
      {dados.tendenciaTemporal.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5" />
              <span>Tendência Temporal</span>
            </CardTitle>
            <CardDescription>
              Evolução do score médio ao longo do tempo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dados.tendenciaTemporal}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="score_medio" 
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  fillOpacity={0.6}
                  name="Score Médio (%)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Melhor Área em Destaque */}
      {dados.estatisticas.melhor_area && (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-green-800">
              <Zap className="w-5 h-5" />
              <span>Área em Destaque</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-green-900">{dados.estatisticas.melhor_area.area}</h3>
                <p className="text-green-700">Melhor performance em fit cultural</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-800">{dados.estatisticas.melhor_area.score_medio}%</p>
                <p className="text-green-600">Score médio</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardAnalytics;